import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PermissionDenied from './PermissionDenied/PermissionDenied';

const ProtectedRoute = ({ children, requireAdmin = false, screenPath = null, isPublic = false }) => {
  const { isAuthenticated, isAdmin, loading, publicScreensLoaded, canAccessScreen, publicScreens } = useAuth();
  const location = useLocation();
  const [showPermissionDenied, setShowPermissionDenied] = useState(false);

  // Special exception: Amplifier Details screens are always accessible in read-only mode
  // Check this BEFORE the loading gate so amp pages render immediately on first page load
  // without waiting for auth/me or /public/screens to resolve — eliminating the blank-page
  // flash that occurred on every full-page first load.
  const isAmplifierDetailsScreen = location.pathname.startsWith('/amplifier-components/') || 
                                   location.pathname.startsWith('/amps/') ||
                                   screenPath?.includes('/amplifier-components/');

  if (isAmplifierDetailsScreen) {
    return children;
  }

  // Wait for both auth loading and public screens to be loaded
  if (loading || !publicScreensLoaded) {
    return <div className="loading">Loading...</div>;
  }

  // Check if this is a public screen first
  const isScreenPublic = isPublic || (screenPath && publicScreens.some(screen => screen.screen_path === screenPath));
  
  if (isScreenPublic) {
    // Public screens don't require authentication
    return children;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check admin requirement first
  if (requireAdmin && !isAdmin) {
    if (!showPermissionDenied) {
      setShowPermissionDenied(true);
    }
    return <PermissionDenied onClose={() => setShowPermissionDenied(false)} />;
  }

  // Check screen-specific permissions
  if (screenPath && !canAccessScreen(screenPath)) {
    if (!showPermissionDenied) {
      setShowPermissionDenied(true);
    }
    return <PermissionDenied onClose={() => setShowPermissionDenied(false)} />;
  }

  return children;
};

export default ProtectedRoute;
